/**
 * Site Configuration
 * Centralized configuration for site-wide settings, social links, and metadata
 */

export const siteConfig = {
  // Basic site info
  name: 'borninsea',
  title: '王海生 borninsea',
  url: 'https://borninsea.com',
  
  // Author info
  author: {
    name: '王海生',
    nameEn: 'Wang Haisheng',
    email: 'contact@borninsea.com',
    bio: '10年AI+医疗产品经验，40年创伤探索之路',
    bioEn: '15 years in AI+Healthcare, 40 years of trauma exploration',
  },
  
  // Social media links
  social: {
    twitter: {
      url: 'https://twitter.com/edwin_uestc',
      handle: '@borninsea',
    },
    github: {
      url: 'https://github.com/wanghaisheng',
      handle: 'borninsea',
    },
    linkedin: {
      url: 'https://www.linkedin.com/in/wanghaisheng',
    },
    youtube: {
      url: 'https://www.youtube.com/@borninsea',
      handle: '@borninsea',
    },
    bilibili: {
      url: 'https://space.bilibili.com/borninsea',
    },
    xiaohongshu: {
      url: 'https://www.xiaohongshu.com/user/profile/borninsea',
    },
  },
  
  // SEO defaults
  seo: {
    defaultTitle: '王海生 borninsea | 在爱的荒漠中求生',
    defaultDescription: '王海生，毕业于电子科技大学生物医学工程专业,来自甘肃静宁,农民的儿子。10年AI+医疗产品经验，40年创伤探索之路。将个人经历产品化，用AI时代的工具和方法论，探索身心修炼与创业的可能性。',
    defaultKeywords: '王海生,borninsea,创伤疗愈,AI创业,产品经理,Challenge100,医疗AI,身心修炼,个人成长',
    ogImage: 'https://borninsea.com/og-image.png',
    twitterCard: 'summary_large_image' as const,
  },
  
  // Analytics IDs and configuration
  analytics: {
    googleAnalytics: {
      measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-KZPJE1XS4V',
      enabled: import.meta.env.PROD,
    },
    googleAdSense: {
      clientId: import.meta.env.VITE_ADSENSE_CLIENT_ID || 'ca-pub-1099284926156735',
      enabled: import.meta.env.PROD,
    },
    microsoftClarity: {
      projectId: import.meta.env.VITE_CLARITY_PROJECT_ID || 'uo85pl5gmi',
      enabled: import.meta.env.PROD,
    },
  },
  
  // Ad slots configuration
  adSlots: {
    banner: { 
      slot: '1234567890', 
      format: 'auto' as const,
      style: { minHeight: '90px' }
    },
    sidebar: { 
      slot: '1234567891', 
      format: 'vertical' as const,
      style: { minHeight: '250px', minWidth: '300px' }
    },
    inArticle: { 
      slot: '1234567892', 
      format: 'rectangle' as const,
      style: { minHeight: '250px' }
    },
    footer: { 
      slot: '1234567893', 
      format: 'horizontal' as const,
      style: { minHeight: '90px' }
    }
  },
  
  // Theme
  theme: {
    primaryColor: '#000000',
    accentColor: '#2563eb',
  },
  
  // Navigation links
  nav: {
    main: [
      { key: 'aboutMe', href: '/about' },
      { key: 'blog', href: '/blog' },
      { key: 'contact', href: '#contact' },
    ],
    resources: [
      { key: 'pearl', href: '/pearl' },
      { key: 'methodology', href: '/methodology' },
      { key: 'faq', href: '/faq' },
    ],
    challenge100: [
      { key: 'spy100', href: '/spy100' },
      { key: 'launch100', href: '/launch100' },
      { key: 'ship100', href: '/ship100' },
      { key: 'grow100', href: '/grow100' },
    ],
  },
  
  // Contact configuration
  contact: {
    // Cal.com booking links
    cal: {
      // Main consultation booking link
      consultation: 'https://cal.com/borninsea/consultation',
      // Quick 15-minute chat
      quickChat: 'https://cal.com/borninsea/15min',
      // Trauma healing session
      traumaSession: 'https://cal.com/borninsea/trauma-session',
      // Product strategy discussion
      productStrategy: 'https://cal.com/borninsea/product-strategy',
      // General meeting
      meeting: 'https://cal.com/borninsea/meeting',
      hit: 'https://cal.com/borninsea/hit',
      


    },
    // Contact methods
    methods: {
      email: 'contact@borninsea.com',
      wechat: 'borninsea',
      telegram: '@borninsea',
    },
    // Availability info
    availability: {
      timezone: 'Asia/Shanghai',
      workingHours: '9:00 - 18:00',
      responseTime: '24小时内回复',
    },
  },
  
  // Footer links
  footer: {
    socialLinks: [
      { platform: 'twitter', icon: 'Twitter' },
      { platform: 'github', icon: 'Github' },
      { platform: 'youtube', icon: 'Youtube' },
      { platform: 'linkedin', icon: 'Linkedin' },
    ],
  },
} as const;

// Type exports for TypeScript support
export type SiteConfig = typeof siteConfig;
export type SocialPlatform = keyof typeof siteConfig.social;
export type CalLinkType = keyof typeof siteConfig.contact.cal;
export type ContactMethod = keyof typeof siteConfig.contact.methods;

// Helper functions
export function getSocialUrl(platform: SocialPlatform): string {
  return siteConfig.social[platform].url;
}

export function getSocialHandle(platform: 'twitter' | 'github' | 'youtube'): string {
  return siteConfig.social[platform].handle;
}

export function getFullTitle(pageTitle?: string): string {
  if (!pageTitle) return siteConfig.seo.defaultTitle;
  return `${pageTitle} | ${siteConfig.name}`;
}

// Contact helper functions
export function getCalLink(type: keyof typeof siteConfig.contact.cal): string {
  return siteConfig.contact.cal[type];
}

export function getContactMethod(method: keyof typeof siteConfig.contact.methods): string {
  return siteConfig.contact.methods[method];
}

export function getAvailabilityInfo(): typeof siteConfig.contact.availability {
  return siteConfig.contact.availability;
}

// LD-JSON structured data generators
export function getPersonSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: siteConfig.author.name,
    alternateName: siteConfig.author.nameEn,
    url: siteConfig.url,
    image: siteConfig.seo.ogImage,
    description: siteConfig.author.bio,
    jobTitle: ['产品经理', '创业者', '创伤探索者'],
    sameAs: [
      siteConfig.social.twitter.url,
      siteConfig.social.github.url,
      siteConfig.social.linkedin.url,
    ],
    knowsAbout: ['AI', '医疗科技', '产品管理', '创伤疗愈', '创业'],
  };
}

export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    alternateName: '王海生个人网站',
    url: siteConfig.url,
    description: siteConfig.seo.defaultDescription,
    inLanguage: ['zh-CN', 'en'],
  };
}

export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    logo: `${siteConfig.url}/logo.png`,
    founder: {
      '@type': 'Person',
      name: siteConfig.author.name,
    },
    description: siteConfig.seo.defaultDescription,
  };
}

